import React, { useEffect, useRef, useState } from "react";
import { plyxApi } from "@/lib/plyxApi";
import { Search, X, Music, Loader2, Play, Pause, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * SoundLibraryDrawer — search + pick a free music track or a Plyx
 * Original sound to attach to your upload.
 *
 * Backed by `GET /api/plyx/sound-library/{featured,search,genres}`.
 *
 * Props:
 *   open            — boolean
 *   onClose         — () => void
 *   onPick          — (sound) => void  (sound has audio_url, sound_id, title, …)
 *   selectedId      — current pick (for ✓ highlight)
 */
const SoundLibraryDrawer = ({ open, onClose, onPick, selectedId }) => {
  const [q, setQ] = useState("");
  const [genre, setGenre] = useState("");
  const [genres, setGenres] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);

  // Load featured + genres on mount.
  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const [feat, gen] = await Promise.all([
          plyxApi.get("/plyx/sound-library/featured?limit=30"),
          plyxApi.get("/plyx/sound-library/genres"),
        ]);
        setItems(feat.data?.items || []);
        setGenres(gen.data?.genres || []);
      } catch {
        toast.error("Couldn't load sound library");
      } finally { setLoading(false); }
    })();
  }, [open]);

  // Debounced search.
  useEffect(() => {
    if (!open) return undefined;
    if (!q.trim() && !genre) return undefined;
    const id = setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (q.trim()) params.set("q", q.trim());
        if (genre) params.set("genre", genre);
        params.set("limit", "30");
        const r = await plyxApi.get(`/plyx/sound-library/search?${params}`);
        setItems(r.data?.items || []);
      } catch {
        toast.error("Search failed");
      } finally { setLoading(false); }
    }, 280);
    return () => clearTimeout(id);
  }, [q, genre, open]);

  // Stop preview when drawer closes.
  useEffect(() => {
    if (!open && audioRef.current) {
      try { audioRef.current.pause(); audioRef.current.src = ""; } catch { /* ignore */ }
      setPlayingId(null);
    }
  }, [open]);

  if (!open) return null;

  const playPreview = (sound) => {
    try {
      if (playingId === sound.sound_id) {
        audioRef.current?.pause();
        setPlayingId(null);
        return;
      }
      if (!audioRef.current) audioRef.current = new Audio();
      audioRef.current.src = sound.audio_url;
      audioRef.current.currentTime = 0;
      audioRef.current.volume = 0.85;
      audioRef.current.play().then(() => setPlayingId(sound.sound_id)).catch(() => {
        toast.error("Preview unavailable");
      });
      audioRef.current.onended = () => setPlayingId(null);
    } catch {
      toast.error("Preview failed");
    }
  };

  const pickIt = (sound) => {
    try { audioRef.current?.pause(); } catch { /* ignore */ }
    setPlayingId(null);
    onPick?.(sound);
    toast.success(`Sound added: ${sound.title}`);
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-end sm:items-center sm:justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="plyx-sound-library-backdrop"
    >
      <div
        className="w-full sm:max-w-lg bg-gradient-to-b from-zinc-900 to-black border-t sm:border border-white/15 sm:rounded-2xl rounded-t-3xl text-white max-h-[88vh] flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="plyx-sound-library-panel"
      >
        <div className="flex items-center gap-3 px-5 pt-5 pb-3 border-b border-white/10">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#22D3EE] to-[#A855F7] flex items-center justify-center">
            <Music className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <div className="font-bold">Sound Library</div>
            <div className="text-[11px] text-white/60">Free music + Plyx originals</div>
          </div>
          <button onClick={onClose} className="rounded-full bg-white/10 hover:bg-white/20 p-1.5" data-testid="plyx-sound-library-close">
            <X className="w-4 h-4"/>
          </button>
        </div>
        <div className="px-5 pt-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40"/>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search a vibe, mood, or song title…"
              className="w-full bg-white/5 border border-white/15 rounded-xl pl-9 pr-3 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#22D3EE]"
              data-testid="plyx-sound-library-search"
            />
          </div>
          <div className="mt-3 flex gap-1.5 overflow-x-auto no-scrollbar pb-2">
            <button
              onClick={() => setGenre("")}
              className={`shrink-0 text-xs font-semibold px-3 py-1 rounded-full transition ${!genre ? "bg-white text-black" : "bg-white/10 text-white/70 hover:bg-white/20"}`}
              data-testid="plyx-sound-library-genre-all"
            >All</button>
            {genres.map((g) => (
              <button
                key={g.slug}
                onClick={() => setGenre(g.slug === genre ? "" : g.slug)}
                className={`shrink-0 text-xs font-semibold px-3 py-1 rounded-full transition flex items-center gap-1.5 ${genre === g.slug ? "bg-white text-black" : "bg-white/10 text-white/70 hover:bg-white/20"}`}
                data-testid={`plyx-sound-library-genre-${g.slug}`}
              ><span>{g.emoji}</span> {g.label}</button>
            ))}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-3" data-testid="plyx-sound-library-list">
          {loading && (
            <div className="flex items-center justify-center py-10 text-white/50">
              <Loader2 className="w-5 h-5 animate-spin mr-2"/> Loading…
            </div>
          )}
          {!loading && items.length === 0 && (
            <div className="text-center text-white/50 py-10 text-sm">No tracks match that vibe yet.</div>
          )}
          <div className="space-y-2">
            {items.map((s) => {
              const isPicked = selectedId && s.sound_id === selectedId;
              const isPlaying = playingId === s.sound_id;
              return (
                <div
                  key={s.sound_id}
                  className={`rounded-xl px-3 py-2.5 border flex items-center gap-3 transition ${isPicked ? "border-[#FFCC2E] bg-[#FFCC2E]/10" : "border-white/10 bg-white/5 hover:bg-white/10"}`}
                  data-testid={`plyx-sound-library-row-${s.sound_id}`}
                >
                  <button
                    onClick={() => playPreview(s)}
                    className="w-10 h-10 rounded-full bg-gradient-to-br from-[#22D3EE] to-[#A855F7] flex items-center justify-center shrink-0"
                    data-testid={`plyx-sound-library-play-${s.sound_id}`}
                    aria-label={isPlaying ? "Pause" : "Play"}
                  >
                    {isPlaying ? <Pause className="w-4 h-4 text-white"/> : (
                      s.thumbnail_emoji ? <span className="text-xl">{s.thumbnail_emoji}</span> : <Play className="w-4 h-4 text-white ml-0.5"/>
                    )}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{s.title}</div>
                    <div className="text-[11px] text-white/60 flex items-center gap-1.5 truncate">
                      {s.artist && <span>{s.artist}</span>}
                      {s.genre && <span>· {s.genre}</span>}
                      {s.duration_sec > 0 && <span>· {Math.round(s.duration_sec)}s</span>}
                      {s.source === "curated" && <span className="text-[#FFCC2E]">· free</span>}
                      {s.source === "plyx" && s.usage_count > 0 && <span>· {s.usage_count} clips</span>}
                    </div>
                  </div>
                  <button
                    onClick={() => pickIt(s)}
                    className={`text-xs font-bold px-3 py-1.5 rounded-full shrink-0 transition ${isPicked ? "bg-[#FFCC2E] text-black" : "bg-white text-black hover:bg-white/90"}`}
                    data-testid={`plyx-sound-library-pick-${s.sound_id}`}
                  >
                    {isPicked ? <span className="flex items-center gap-1"><Check className="w-3.5 h-3.5"/> Picked</span> : "Use"}
                  </button>
                </div>
              );
            })}
          </div>
          <div className="text-[10px] text-white/30 text-center mt-4 px-2">
            All curated tracks are licensed free for creator use. Plyx originals stay credited to the original creator.
          </div>
        </div>
      </div>
    </div>
  );
};

export default SoundLibraryDrawer;
